/**
 * Data-driven element flow. Given the parsed StyleDefs and the current
 * paragraph's style + emptiness, decide what Enter / Tab / Shift+Tab do. All
 * transitions read from style_enter / style_tab_after / style_tab_before —
 * the only hardcoded rule is the empty dialogue-family → Action escape hatch.
 */
import type { StyleDef } from "../format/types";

export type FlowAction =
  | { kind: "newPara"; style: string } // split: new paragraph gets this style
  | { kind: "retype"; style: string }; // change the current paragraph's style

/** Elements whose empty paragraph collapses to Action on Enter (Fade In escape). */
const DIALOGUE_FAMILY = new Set(["Character", "Parenthetical", "Dialogue"]);
const ESCAPE_TARGET = "Action";

/** Ordered element cycle for Shift+Tab (built-ins minus Normal Text). */
const ELEMENT_CYCLE = [
  "Scene Heading",
  "Action",
  "Character",
  "Parenthetical",
  "Dialogue",
  "Transition",
  "Shot",
];

/** Enter behavior. */
export function enterAction(
  styles: Map<string, StyleDef>,
  current: string,
  isEmpty: boolean,
): FlowAction {
  if (isEmpty && DIALOGUE_FAMILY.has(current)) {
    return { kind: "retype", style: ESCAPE_TARGET };
  }
  const enter = styles.get(current)?.styleEnter;
  return { kind: "newPara", style: enter ?? current };
}

/** Tab behavior. */
export function tabAction(
  styles: Map<string, StyleDef>,
  current: string,
  isEmpty: boolean,
): FlowAction {
  const def = styles.get(current);
  if (isEmpty) {
    // Cycle the current (empty) paragraph via style_tab_before.
    return { kind: "retype", style: def?.styleTabBefore ?? current };
  }
  // At the end of a non-empty paragraph, create the style_tab_after element.
  return { kind: "newPara", style: def?.styleTabAfter ?? def?.styleEnter ?? current };
}

/**
 * Fade In-style element shortcuts: Ctrl/Cmd + builtin_index. Data-driven from
 * the document's styles, so it matches Fade In exactly:
 *   Mod-0 Normal Text · Mod-1 Scene Heading · Mod-2 Action · Mod-3 Character ·
 *   Mod-4 Parenthetical · Mod-5 Dialogue · Mod-6 Transition · Mod-7 Shot.
 * Returns [{ key: "Mod-3", style: "Character" }, …] for indices 0–9.
 */
export function elementShortcuts(
  styles: Map<string, StyleDef>,
): Array<{ key: string; style: string }> {
  const out: Array<{ key: string; style: string }> = [];
  for (const style of styles.values()) {
    const i = style.builtinIndex;
    if (i !== undefined && Number.isInteger(i) && i >= 0 && i <= 9) {
      out.push({ key: `Mod-${i}`, style: style.name });
    }
  }
  return out;
}

/**
 * The two most likely next elements for the current one (from its enter/tab
 * flow), used to surface prominent quick buttons in the ElementSwitcher.
 */
export function nextSuggestions(
  styles: Map<string, StyleDef>,
  current: string,
): string[] {
  const def = styles.get(current);
  const out: string[] = [];
  const add = (s?: string) => {
    if (s && !out.includes(s)) out.push(s);
  };
  add(def?.styleEnter);
  add(def?.styleTabAfter);
  for (const fallback of ["Action", "Character", "Dialogue"]) {
    if (out.length >= 2) break;
    add(fallback);
  }
  return out.slice(0, 2);
}

/** Shift+Tab cycles the current element backward through the built-in list. */
export function shiftTabAction(
  _styles: Map<string, StyleDef>,
  current: string,
): FlowAction {
  const idx = ELEMENT_CYCLE.indexOf(current);
  if (idx === -1) return { kind: "retype", style: ESCAPE_TARGET };
  const prev = ELEMENT_CYCLE[(idx - 1 + ELEMENT_CYCLE.length) % ELEMENT_CYCLE.length]!;
  return { kind: "retype", style: prev };
}
